import { Component, input, OnChanges, OnInit } from '@angular/core';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { DateService } from 'src/app/services/date-service/date.service';
import { FiltersService } from 'src/app/services/filters-service/filters.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Project } from 'src/app/entities/project/project';
import { User } from 'src/app/entities/user/user';
import { CustomDateFilterComponent, CustomDateFilterOptions, CustomDatesCat, DateFiltersValues } from '../../atoms/custom-date-filter/custom-date-filter.component';

/**
 * Enum to define the available view types for the weekly report display
 */
enum ViewType {
  Projects = 'projects',
  Users = 'users'
}

/**
 * Interface representing a weekly summary for a project or user
 */
interface WeeklySummary {
  id: string;
  name: string;
  color: string;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
  total: number;
}


/**
 * Displays weekly reports, allow users to switch between project and views.
 *
 * Provides a summary of time spent on each project or by each user for the 
 * week.
 * Implements OnChanges to react to input changes and `OnInit` for component 
 * initialization.
 *
 * TODO: Reformat component so it gets synchronized with the actual filters on
 * `FiltersService`.
 */
@Component({
  selector: 'app-reports-weekly',
  imports: [
    CustomDateFilterComponent,
    CommonModule,
    MatTableModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './reports-weekly.component.html',
  styleUrl: './reports-weekly.component.scss'
})
export class ReportsWeeklyComponent implements OnChanges, OnInit {
  // Input time tracks from parent component
  timeTracks = input<TimeTrack[]>([]);

  // Input projects and users
  projects = input<Project[]>([]);
  users = input<User[]>([]);

  // Form group for the view type selector
  protected viewForm = new FormGroup({
    viewType: new FormControl(ViewType.Projects, Validators.required)
  });

  // Expose enum to template
  readonly viewTypes = ViewType;

  // Current week start and end dates
  weekStart = new Date();
  weekEnd = new Date();

  // Column definitions for the table
  displayedColumns: string[] = [
    'title',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
    'total'
  ];

  // Weekly data for the table
  weeklyData: WeeklySummary[] = [];

  dateFilterOptions: CustomDateFilterOptions = {
    default: CustomDatesCat.Week,
    categories: {
      week: true
    }
  }

  constructor(
    protected dateService: DateService,
    protected filters: FiltersService
  ) {
    // Initialize to current week
    this.setCurrentWeek();
  }

  ngOnInit(): void {
    this.viewForm.get('viewType')?.valueChanges.subscribe(() => {
      this.processWeeklyData();
    });
  }

  ngOnChanges(): void {
    this.processWeeklyData();
  }

  /**
   * Sets the current week dates (Monday to Sunday)
   */
  private setCurrentWeek(): void {
    const today = new Date();
    const day = today.getDay(); // 0 is Sunday, 1 is Monday, etc.

    // Calculate days to subtract to get to Monday (or use previous Monday if today is Sunday)
    const daysToMonday = day === 0 ? 6 : day - 1;

    // Set week start to Monday
    this.weekStart = new Date(today);
    this.weekStart.setDate(today.getDate() - daysToMonday);
    this.weekStart.setHours(0, 0, 0, 0);

    // Set week end to Sunday
    this.weekEnd = new Date(this.weekStart);
    this.weekEnd.setDate(this.weekStart.getDate() + 6);
    this.weekEnd.setHours(23, 59, 59, 999);

    // Update filters
    this.setTracksFilterDates(
      this.dateService.getLocalIsosString(this.weekStart),
      this.dateService.getLocalIsosString(this.weekEnd)
    );
  }

  /**
   * Processes time tracks to generate weekly data
   */
  private processWeeklyData(): void {
    const tracks = this.timeTracks();
    if (!tracks || tracks.length === 0) {
      this.weeklyData = [];
      return;
    }

    const weeklyMap = new Map<string, WeeklySummary>();
    const viewType = this.viewForm.get('viewType')?.value;

    // Process each time track
    for (const track of tracks) {
      const trackDate = new Date(track.from);
      const dayOfWeek = trackDate.getDay(); // 0 is Sunday, 1 is Monday, etc.

      // Skip tracks outside the current week
      if (trackDate < this.weekStart || trackDate > this.weekEnd) {
        continue;
      }

      // Create a unique key based on view type
      let key: string;
      let name: string;
      let color: string;

      if (viewType === ViewType.Users && track.author) {
        // For user view, group by user
        key = track.author.id;
        name = track.author.name;
        color = '#6495ED'; // Default color for users
      } else {
        // For project view, group by project
        key = track.project?.id || 'no-project';
        name = track.project?.name || 'No Project';
        color = track.project?.color || '#cccccc';
      }

      if (!weeklyMap.has(key)) {
        weeklyMap.set(key, {
          id: key,
          name: name,
          color: color,
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0,
          total: 0
        });
      }

      const entry = weeklyMap.get(key)!;
      const hours = this.secondsToHours(track.time_spent || 0);

      // Add hours to the appropriate day
      switch (dayOfWeek) {
        case 1: // Monday
          entry.monday += hours;
          break;
        case 2: // Tuesday
          entry.tuesday += hours;
          break;
        case 3: // Wednesday
          entry.wednesday += hours;
          break;
        case 4: // Thursday
          entry.thursday += hours;
          break;
        case 5: // Friday
          entry.friday += hours;
          break;
        case 6: // Saturday
          entry.saturday += hours;
          break;
        case 0: // Sunday
          entry.sunday += hours;
          break;
      }

      // Update total
      entry.total = entry.monday + entry.tuesday + entry.wednesday +
        entry.thursday + entry.friday + entry.saturday + entry.sunday;
    }

    // Convert map to array and sort by name
    this.weeklyData = Array.from(weeklyMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Converts seconds to hours with appropriate precision
   */
  private secondsToHours(seconds: number): number {
    return seconds / 3600;
  }

  /**
   * Format hours for display
   */
  formatHours(hours: number): string {
    if (hours === 0) return '-';
    if (hours < 0.01) return `${Math.round(hours * 3600)}s`;
    if (hours < 0.1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(1)}h`;
  }

  /**
   * Handle date filter selection
   */
  setDateFilterPeriods(event: DateFiltersValues): void {
    this.weekStart = new Date(event.startDate);
    this.weekEnd = new Date(event.endDate);

    this.setTracksFilterDates(
      this.dateService.getLocalIsosString(this.weekStart),
      this.dateService.getLocalIsosString(this.weekEnd)
    );

    this.processWeeklyData();
  }

  /**
   * Set time track filters for date range
   */
  protected setTracksFilterDates(start: string, end: string): void {
    this.filters.setTimeTracksFilters({
      from: start,
      to: end
    });
  }

  /**
   * Calculate total hours for all tracks
   */
  get totalHours(): number {
    return this.weeklyData.reduce((sum, track) => sum + track.total, 0);
  }

  /**
   * Calculate total hours for a specific day of the week
   */
  getDayTotal(day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'): number {
    return this.weeklyData.reduce((sum, track) => sum + track[day], 0);
  }
}
